import { Component, effect, inject, input, computed, signal, viewChild, ViewContainerRef, OnDestroy, OnInit, Signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { EChartsOption } from 'echarts';
import { NgxEchartsDirective, provideEcharts } from 'ngx-echarts';

import { ComponentRegistry, RegisterComponent } from '../../components/component-lookup';
import { VisualBaseComponent } from '../visual-base.component';
import { DecayMetric, DecayMetricData } from '../../services/metric.service';
import { DateRange, StateService } from '../../services/state.service';
import { VisualService } from '../../services/visual.service';
import { DateService } from '../../services/date.service';
import { SystemSettingService } from '../../services/system.setting.service';

import { ChartInfoBoxComponent } from '../chart-info-box/chart-info-box.component';
import { FlexItem } from '../../../shared/services/product.service';
import { InfoIconDialogComponent } from "../info-icon-dialog/info-icon-dialog.component";
import { ToggleComponent } from "../../components/toggle/toggle.component";


interface KpiValues {
  decayFactor: number;
  cashFlow: number;
  straightLineDeclineWal: number;
  pctDeclineWal: number;
}


@RegisterComponent('DecayComponent')
@Component({
  selector: 'app-decay',
  standalone: true,
  imports: [CommonModule, FormsModule, NgxEchartsDirective, ChartInfoBoxComponent, InfoIconDialogComponent, ToggleComponent],
  templateUrl: './decay.component.html',
  styleUrl: './decay.component.scss',
  providers: [provideEcharts()],
})
export class DecayComponent extends VisualBaseComponent {
  content = viewChild.required('content', { read: ViewContainerRef });
  children = input.required<FlexItem[]>();

  readonly id = input.required<number>();
  readonly institution = inject(StateService).institution;
  readonly filteredGroup = inject(StateService).filteredGroup;
  readonly dateRange = inject(StateService).dateRange;
  private dateService = inject(DateService);
  private systemSettingService = inject(SystemSettingService);


  private xAxisData: string[] = [];
  private yAxisData1: (number | undefined)[] = [];
  private yAxisData2: (number | undefined)[] = [];
  public decayType = input<string>('actual');
  public title = input<string>('Balance');
  readonly dialogInformationId = input<string>('');
  readonly kpiDialogInformationId = input<string>('');
  public isActual = computed(() => this.decayType() === 'actual');

  private noEnoughDisplayData = false;
  private showChartMessage = false;
  private noDataMessage =
    "Given the significant\n" +
    "volatility in the balances,\n" +
    "we are unable to reasonably\n" +
    "estimate the split between\n" +
    "stable and nonstable balances.\n" +
    "It is recommended that a\n" +
    "more granular analysis be\n" +
    "performed using instrument-\n" +
    "level data to accurately\n" +
    "access the split between\n" +
    "stable and nonstable balances.";


  sigEventIndValue: number = 0;

  KpiValues: KpiValues = {
    decayFactor: 0,
    cashFlow: 0,
    straightLineDeclineWal: 0,
    pctDeclineWal: 0,
  };

  ngOnInit(): void {
    this.systemSettingService.getSystemSettingValue("nmd_sig_event_ind")
      .subscribe(value => {
        this.sigEventIndValue = Number(value);
      });
  }


  override initOptions = { height: 400 };
  override baseOptions: EChartsOption = {
    grid: {
      left: 10,
      right: 25,
      top: 20,
      bottom: 40,
      containLabel: true,
    },
    legend: {
      selectedMode: true,
    },
    title: {
      show: false,
    },
    xAxis: {
      type: 'category',
      show: true,
      axisLabel: {
        color: '#243b5b',
      },
    },
    yAxis: {
      type: 'value',
      show: true,
      axisLabel: {
        color: '#243b5b',
      },
    },
    axisLabel: {
      color: '#002f8e',
    },
    series: [
      {
        type: 'line',
        symbol: 'none',
      },
      {
        type: 'line',
        symbol: 'none',
      },
    ],
  };
  override tooltipOptions: EChartsOption = {
    tooltip: {
      trigger: 'axis',
      valueFormatter: (value) =>
        value != null ? VisualService.getValueDisplay(Math.trunc(Number(value)), 'USDM') : '--',
    },
  };
  override printOptions: EChartsOption = {
    animation: false,
    series: [
      {
        type: 'line',
        symbol: 'none',
      },
      {
        type: 'line',
        symbol: 'none',
        symbolSize: 7,
        lineStyle: {
          type: [20, 5],
          dashOffset: 5,
        },
      },
    ],
  };

  noData = false;
  futureDates: Date[] = [];
  displayTypeMetric: string | undefined;

  constructor() {
    super();
    effect(() => {

      const truncationValue = this.systemSettingService.getSelectedSystemSetting();
      const covidAdjusted = this.systemSettingService.getAdjustedForCovid();
      const dateRange = this.dateRange();
      const metric = this.metricService.selectedDecayMetric();
      this.displayTypeMetric = metric?.displayType;
      if (this.chartInit() && dateRange && metric) {
        this.dateService.getFutureDates(this.institution().institutionType).subscribe((result) => {
          this.futureDates = result.labels;
        });
        this.updateVisual(dateRange, metric, truncationValue, covidAdjusted);
      }
    });
  }

  public updateVisual(dateRange: DateRange, metric: DecayMetric, truncationValue: string | undefined, adjustedForCovid: boolean) {
    this.metricService.getDecayMetricData(this.institution().iid, metric.metricId)?.subscribe((decayData) => {
      if (decayData.filter((v) => v != null).length == 0) {
        this.echartsInstance?.clear();
        this.chartOptions = this.noDataOptions;
        this.noData = true;
        this.KpiValues = {
          decayFactor: 0, cashFlow: 0, straightLineDeclineWal: 0, pctDeclineWal: 0
        };
      } else {
        if (this.noData) {
          this.setOptions();
          this.noData = false;
        }
        this.updateForecastStates(decayData, dateRange, metric.metricId, truncationValue, adjustedForCovid);

        this.updateOptions = {
          xAxis: {
            data: this.xAxisData,
          },
          yAxis: [
            {
              name: this.noEnoughDisplayData ? "" : "Balance ('000's)",
              nameGap: 8,
            },
          ],
          series: [
            {
              name: "Straight Line",
              data: this.yAxisData1,
              color: this.colors[0],
              symbol: "line",
              symbolSize: 0,
            },
            {
              name: "Decline Decay",
              data: this.yAxisData2,
              color: this.colors[1],
              lineStyle: {
                type: "dashed",
              },
              symbol: "line",
              symbolSize: 0,
            },
          ],
          graphic:
          {
            type: "text",
            left: "center",
            top: "middle",
            style: {
              text: this.showChartMessage && !this.isActual() ? this.noDataMessage : (this.noEnoughDisplayData ? "No Data Available" : ""),
              fontSize: 20,
              fontWeight: "bold",
              fill: "#999",
            },
          }
        };

      }
      this.echartsInstance?.hideLoading();
    });
  }

  updateForecastStates(
    decayData: DecayMetricData[],
    dateRange: DateRange,
    metricId: string,
    truncationValue: string | undefined,
    adjustedForCovid: boolean
  ) {
    const actTodayActual = this.calculateActToday(decayData, dateRange.endDate);

    this.yAxisData1 = [];
    this.yAxisData2 = [];
    this.showChartMessage = false;

    if (actTodayActual <= 0) {
      this.xAxisData = [];
      this.noEnoughDisplayData = true;
      this.createKpiComponent("");
      return;
    }

    const cleanedData = this.trimLeadingZeros(decayData);

    const filteredDateRange = this.getDateRange(
      cleanedData.filteredData,
      dateRange.startDate,
      dateRange.endDate
    );

    const medians = this.calculateGrowtRateMedians(
      cleanedData.filteredData,
      dateRange.startDate,
      dateRange.endDate
    );

    const stableBalances = this.calculateStableBalance(
      filteredDateRange.data,
      medians.minGrowthRateMedian,
      medians.maxGrowthRateMedian,
      dateRange.startDate,
      adjustedForCovid
    );

    const avgStableBalances = this.getAbvStableBalance(stableBalances);

    const adfActual = this.calculateMedian(
      cleanedData.filteredData
        .filter(
          item =>
            item.standardAverageBalanceVolatile != null &&
            new Date(item.reportDate) >= dateRange.startDate &&
            new Date(item.reportDate) <= dateRange.endDate
        )
        .map(item => item.standardAverageBalanceVolatile)
    );

    let adfStableBal = this.calculateMedian(
      avgStableBalances.slice(filteredDateRange.quartersAddedBeforeRange)
    );

    const qdfActual = adfActual ? (1 - (1 - adfActual) ** 0.25) : 0;

    if (adfActual ) {
      adfStableBal = adfStableBal ? adfStableBal : 0;
      const nonStableBalance =
        (adfActual - adfStableBal) *
        (actTodayActual / 4) *
        filteredDateRange.quarters;

      this.showChartMessage = adfStableBal >= adfActual || nonStableBalance > actTodayActual;


      const nonStableToActualBal = nonStableBalance / actTodayActual;

      const slCfActual = actTodayActual * (adfActual / 4);
      const slCfStableBal =
        (actTodayActual - nonStableBalance) * (adfActual / 4);

      this.KpiValues.cashFlow = this.isActual() ? slCfActual : slCfStableBal;
      this.KpiValues.decayFactor = this.isActual()
        ? qdfActual
        : nonStableToActualBal;

      // if (this.isActual()) {
      //   console.log("startValue:" + actTodayActual)
      //   console.log("minGrowthRateMedian: " + medians.minGrowthRateMedian);
      //   console.log("maxGrowthRateMedian: " + medians.maxGrowthRateMedian);
      //   console.log("AdfActual: " + adfActual);
      //   console.log("AdfStableBal: " + adfStableBal);
      //   console.log("QdfActual: " + qdfActual);
      //   console.log("nonStableBalance: " + nonStableBalance);
      //   console.log("slCfActual: " + slCfActual);
      //   console.log("slCfStableBal: " + slCfStableBal);
      //   console.log("nonStableToActualBal: " + nonStableToActualBal);
      //   console.log("stable balances:");
      //   console.log(stableBalances);
      // }

      const slCfStableBalInitial = slCfStableBal + nonStableBalance;

      const chartData = this.generateStraightLineData(
        actTodayActual,
        this.isActual() ? slCfActual : slCfStableBal,
        dateRange.endDate,
        this.isActual(),
        slCfStableBalInitial,
        truncationValue
      );

      this.xAxisData = chartData.xAxisData;
      this.yAxisData1 = chartData.yAxisData1;

      const declineData = this.generateDecayData(
        actTodayActual,
        qdfActual,
        this.yAxisData1.length,
        this.isActual(),
        nonStableBalance
      );

      this.yAxisData2 = declineData.yAxisData2;
    }

    this.noEnoughDisplayData =
      this.yAxisData1.length <= 2 && this.yAxisData2.length <= 2;

    this.createKpiComponent(metricId);

    if (this.showChartMessage && !this.isActual()) {
      this.xAxisData = [];
      this.yAxisData1 = [];
      this.yAxisData2 = [];
      this.noEnoughDisplayData = true;
      this.createKpiComponent("");
    }
  }


  calculateGrowtRateMedians(decayMetricData: DecayMetricData[], minDate: Date, maxDate: Date) {
    const isInDateRange = (dateStr: Date) =>
      new Date(dateStr) >= minDate && new Date(dateStr) <= maxDate;

    let values = decayMetricData
      .filter(item => isInDateRange(item.reportDate))
      .map(item => item.growthRate)
      .filter((val): val is number => val != null);

    //Do not include in Median calculation
    if (values[0] === 0) {
      values = values.slice(1);
    }

    values = values.sort((a, b) => a - b);

    const minValues = values.filter(val => val <= 0);
    const maxValues = values.filter(val => val > 0);

    return {
      minGrowthRateMedian: this.calculateMedian(minValues),
      maxGrowthRateMedian: this.calculateMedian(maxValues),

    };
  }

  calculateMedian = (arr: number[]) => {
    const orderedArray = arr.sort((a, b) => a - b);
    const len = orderedArray.length;
    if (len === 0) return null;
    const middle = Math.floor(len / 2);
    return len % 2 === 0 ? (orderedArray[middle - 1] + orderedArray[middle]) / 2 : orderedArray[middle];
  };

  calculateStableBalance(decayData: DecayMetricData[], minGrowthRateMedian: number | null, maxGrowthRateMedian: number | null, originalStartDate: Date, adjustedForCovid: boolean) {

    const firstDate = decayData[0]?.reportDate;
    const wasTrimmed: boolean = new Date(firstDate) > new Date(originalStartDate);

    const growthRates = decayData
      .filter(item => item.growthRate != null && item != undefined)

    const stableBalances: { initialStableBalRateOfChange: number[]; stableBalance: number[] } = {
      initialStableBalRateOfChange: [],
      stableBalance: [],
    };

    let stableBalance: number = 0;
    let balRate: number = 0;
    let isFirst: boolean = true;
    let qtrsCount: number = 1;

    if ((minGrowthRateMedian !== null && minGrowthRateMedian != undefined) && (maxGrowthRateMedian !== null && maxGrowthRateMedian != undefined)) {
      growthRates.forEach(item => {

        if (item.growthRate != null) {
          if (isFirst) {
            stableBalance = item.value!;
            balRate = 0;
            isFirst = false;
          }
          else {
            qtrsCount++;
            balRate =
              item.growthRate < minGrowthRateMedian
                ? minGrowthRateMedian
                : item.growthRate > maxGrowthRateMedian
                  ? maxGrowthRateMedian
                  : item.growthRate;

            const meetsGrowthRateCondition = Math.abs(item.growthRate) >= this.sigEventIndValue;
            const meetsCovidCondition = adjustedForCovid
              ? new Date(item.reportDate).toISOString().split('T')[0] < new Date("2020-03-31").toISOString().split('T')[0] ||
              new Date(item.reportDate).toISOString().split('T')[0] > new Date("2021-12-31").toISOString().split('T')[0]
              : true;

            if (meetsGrowthRateCondition && meetsCovidCondition) {
              stableBalance = item.value! * 0.90;
            } else {
              stableBalance = Math.min(item.value!, (stableBalance * (1 + balRate)));
            }


            //Check this logic
            if (qtrsCount <= 5 && !wasTrimmed && new Date(item.reportDate) <= new Date(originalStartDate)) {
              stableBalance = item.value!
            }
          }
          stableBalances.initialStableBalRateOfChange.push(balRate);
          stableBalances.stableBalance.push(stableBalance);
        }

      });
    }


    return stableBalances.stableBalance;
  }

  getAbvStableBalance(stable_balance: number[]): number[] {
    const result: number[] = [];

    for (let i = 0; i < stable_balance.length; i++) {
      const windowStart = Math.max(0, i - 3); // 4-period rolling window
      const window = stable_balance.slice(windowStart, i + 1);

      const mean = window.reduce((sum, val) => sum + val, 0) / window.length;
      const variance = window.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / window.length;
      const stdDev = Math.sqrt(variance);

      result.push(mean === 0 ? 0 : stdDev / mean);
    }

    // if (this.isActual()) {
    //   console.log("AVG STABLE VALUES")
    //   console.log(result)

    // }
    return result;
  }

  calculateActToday(decayMetricData: DecayMetricData[], maxDate: Date): number {
    const targetDate = maxDate.toISOString().split('T')[0];
    const matchingItem = decayMetricData.find(
      (item) => new Date(item.reportDate).toISOString().split('T')[0] === targetDate,
    );
    return (matchingItem?.value ?? 0)
  }

  getDateRange(decayData: DecayMetricData[], minDate: Date, maxDate: Date) {

    const mainRange = decayData.filter(item => {
      const date = new Date(item.reportDate);
      return date >= minDate && date <= maxDate;
    });

    const quarters = mainRange.length;

    // Get the index of the first item in the range
    const firstIndex = decayData.findIndex(item =>
      new Date(item.reportDate).getTime() === new Date(mainRange[0].reportDate).getTime()
    );

    // Calculate how many items we can actually add before the main range
    // If available we want to add 4 items before, but sometimes there are less than four and we want to keep track of that
    const itemsBeforeCount = Math.min(4, firstIndex);

    // Take the items before the first index
    const extendedRange = decayData.slice(firstIndex - itemsBeforeCount, firstIndex);

    const fullRange = [...extendedRange, ...mainRange];

    return { data: fullRange, quarters: quarters, quartersAddedBeforeRange: itemsBeforeCount };

  }

  trimLeadingZeros(data: DecayMetricData[]): { filteredData: DecayMetricData[] } {

    const firstValidIndex = data.findIndex(item =>
      item.value !== null && item.value !== 0
    );

    return {
      filteredData: firstValidIndex === -1 ? [] : data.slice(firstValidIndex)
    };
  }

  getQuarterEndDate(date: Date): Date {
    const month = date.getMonth();
    const year = date.getFullYear();

    if (month >= 0 && month <= 2) {
      return new Date(year, 2, 31); // Q1
    } else if (month >= 3 && month <= 5) {
      return new Date(year, 5, 30); // Q2
    } else if (month >= 6 && month <= 8) {
      return new Date(year, 8, 30); // Q3
    } else {
      return new Date(year, 11, 31); // Q4
    }
  }

  addQuarters(date: Date, quarters: number): Date {
    const newDate = new Date(date);
    // Set to first day of the month to avoid month overflow
    newDate.setDate(1);
    newDate.setMonth(newDate.getMonth() + quarters * 3);
    return this.getQuarterEndDate(newDate);
  }

  createKpiComponent(metricId: string) {
    this.content().clear();
    this.children().forEach(child => {
      const component = ComponentRegistry.get(child.name);
      if (component) {
        const componentRef = this.content().createComponent(component);
        if (componentRef) {
          Object.keys(child.data).forEach(key => {
            componentRef.setInput(key, child.data[key]);
          });
          componentRef.setInput('metricId', metricId);
          componentRef.setInput('value', this.getKpiValue(child.data.type));
          componentRef.setInput('subjectType', this.isActual() ? 1 : 2);
        } else {
          console.error(`Failed to create component: ${child.name}`);
        }
      }
    });
  }

  getKpiValue(kpiType: string): Signal<(number | null)> {
    const kpiMap: Record<string, number | null> = {
      cashFlow: this.noEnoughDisplayData ? null : this.KpiValues.cashFlow,
      decayFactor: this.noEnoughDisplayData ? null : this.KpiValues.decayFactor * 100,
      straightLineDeclineWal: this.noEnoughDisplayData ? null : this.KpiValues.straightLineDeclineWal,
      pctDeclineWal: this.noEnoughDisplayData ? null : this.KpiValues.pctDeclineWal
    };

    return computed(() => kpiMap[kpiType] ?? null);
  }

  generateStraightLineData(
    startingValueActual: number,
    cashFlow: number,
    endingDate: Date,
    isActual: boolean,
    firstCashFlow: number,
    truncationValue: string | undefined
  ) {
    const xAxisData: string[] = [];
    const yAxisData1: (number | undefined)[] = [];
    let straightProductSum: number = 0;
    let straightCashFlowSum: number = 0;
    let qtrCounter: number = 1;
    let isFirst: boolean = true;
    let previousValue: number = startingValueActual;
    const truncationNum = truncationValue ? Number(truncationValue) : 400; //400 is equivalent to 100 years

    yAxisData1.push(startingValueActual / 1000);
    xAxisData.push(endingDate.toLocaleString('en-US', { month: 'short', year: 'numeric' }));


    while (previousValue > cashFlow && (qtrCounter <= truncationNum)) {

      endingDate = this.addQuarters(endingDate, 1);

      if (!isActual && isFirst) {
        previousValue = startingValueActual - firstCashFlow;
        yAxisData1.push(previousValue / 1000);
        xAxisData.push(endingDate.toLocaleString('en-US', { month: 'short', year: 'numeric' }));
        isFirst = false;
        straightProductSum += firstCashFlow;
        straightCashFlowSum += firstCashFlow;

      }
      else {
        previousValue = previousValue - cashFlow;
        yAxisData1.push(previousValue / 1000);
        xAxisData.push(endingDate.toLocaleString('en-US', { month: 'short', year: 'numeric' }));
        straightProductSum += (cashFlow * qtrCounter);
        straightCashFlowSum += cashFlow;
      }

      qtrCounter++;
    }

    yAxisData1.push(0);
    endingDate = this.addQuarters(endingDate, 1);
    xAxisData.push(endingDate.toLocaleString('en-US', { month: 'short', year: 'numeric' }));

    straightProductSum += (previousValue * qtrCounter);
    straightCashFlowSum += previousValue;

    this.KpiValues.straightLineDeclineWal = (straightProductSum / straightCashFlowSum) / 4;
    return { xAxisData, yAxisData1 };
  }

  generateDecayData(
    startingValueActual: number,
    qdfActual: number,
    dataLength: number,
    isActual: boolean,
    nonStableBalance: number
  ) {
    const yAxisData2: (number | undefined)[] = [];
    let cashFlow = 0;
    let currentCashFlow = 0;
    let secondAdjustedValue = ((startingValueActual - nonStableBalance) - (startingValueActual - nonStableBalance) * (qdfActual)) / 1000;
    let previousValue = startingValueActual / 1000;
    yAxisData2.push(previousValue); //insertInitial Value
    let isFirst = true;
    let secondAdjustedCashFlow = previousValue - secondAdjustedValue;

    let pctProductSum: number = 0;
    let pctCashFlowSum: number = 0;

    for (let quarter = 1; quarter < dataLength; quarter++) {

      //Last item value is 0 and the cashflow is the previous value
      if (quarter == (dataLength - 1)) {
        yAxisData2.push(0);
        const currentSum = previousValue * quarter;
        pctProductSum += currentSum;
        pctCashFlowSum += previousValue;
        break;

      }

      if (!isActual && isFirst) {
        yAxisData2.push(secondAdjustedValue);
        previousValue = secondAdjustedValue;
        currentCashFlow = secondAdjustedCashFlow;
        isFirst = false;

      }
      else {
        cashFlow = previousValue * qdfActual;
        previousValue = previousValue - cashFlow;
        yAxisData2.push(previousValue);
        currentCashFlow = cashFlow;

      }

      const currentSum = currentCashFlow * quarter;
      pctProductSum += currentSum;
      pctCashFlowSum += currentCashFlow;

    }

    this.KpiValues.pctDeclineWal = (pctProductSum / pctCashFlowSum) / 4;
    return { yAxisData2 };
  }

}